import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Printer, Plus, SlidersHorizontal, Undo2, Search, Loader2, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, StatCard, TxnBadge, Money, ExportButtons, FilterSelect } from "@/components/common";
import EmployeePicker from "@/components/EmployeePicker";
import DatePicker from "@/components/DatePicker";
import { AdminTxnDialog, ReverseDialog } from "@/components/TxnDialogs";
import { api, errMsg, download } from "@/lib/api";
import { inr, fmtDate, fmtDateTime, TXN, monthLabel } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TYPE_OPTS = Object.entries(TXN).map(([value, t]) => ({ value, label: t.label }));

function LedgerTable({ entries, isAdmin, onReverse }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="ledger-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
          <tr>
            <th className="px-3 py-2.5 text-left">Date</th><th className="px-3 py-2.5 text-left">Transaction type</th><th className="px-3 py-2.5 text-left">Reference</th>
            <th className="px-3 py-2.5 text-right">Debit (₹)</th><th className="px-3 py-2.5 text-right">Credit (₹)</th><th className="px-3 py-2.5 text-right">Balance (₹)</th>
            <th className="px-3 py-2.5 text-left hidden md:table-cell">Recorded by</th>{isAdmin && <th className="px-3 py-2.5 no-print" />}
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 && <tr><td colSpan={8} className="text-center text-slate-500 py-10">No transactions match the filters.</td></tr>}
          {entries.map((e, i) => (
            <tr key={e.id} className={`border-t border-slate-100 hover:bg-slate-50 ${e.reversed ? "opacity-60" : ""}`} data-testid={`ledger-row-${i}`}>
              <td className="px-3 py-2.5 whitespace-nowrap">{fmtDate(e.date)}</td>
              <td className="px-3 py-2.5"><TxnBadge type={e.type} reversed={e.reversed} /></td>
              <td className="px-3 py-2.5 max-w-[260px]"><div className="truncate">{e.reference || (e.payroll_month ? "" : "—")}</div>{e.payroll_month && <div className="text-xs text-blue-800" data-testid={`ledger-payroll-month-${i}`}>Payroll month: {monthLabel(e.payroll_month)}</div>}{e.remarks && <div className="text-xs text-slate-500 truncate">{e.remarks}</div>}</td>
              <td className="px-3 py-2.5 text-right text-red-700" data-testid={`ledger-debit-${i}`}><Money value={e.debit} blankZero /></td>
              <td className="px-3 py-2.5 text-right text-emerald-700" data-testid={`ledger-credit-${i}`}><Money value={e.credit} blankZero /></td>
              <td className="px-3 py-2.5 text-right font-semibold" data-testid={`ledger-balance-${i}`}><Money value={e.balance} /></td>
              <td className="px-3 py-2.5 text-xs text-slate-500 hidden md:table-cell whitespace-nowrap">{e.created_by}<div>{fmtDateTime(e.created_at)}</div></td>
              {isAdmin && (
                <td className="px-3 py-2.5 text-right no-print">
                  {e.type !== "reversal" && !e.reversed && (
                    <Button variant="ghost" size="sm" className="text-slate-500 hover:text-red-700" onClick={() => onReverse(e)} data-testid={`reverse-btn-${i}`}><Undo2 className="h-4 w-4" /></Button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Ledger() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", type: "", q: "" });
  const [dlg, setDlg] = useState("");
  const [rev, setRev] = useState(null);

  const load = useCallback(() => {
    if (!code) { setData(null); return; }
    api.get(`/ledger/${code}`, { params: filters }).then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e)); setData(false); });
  }, [code, filters]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const emp = data?.employee;
  const s = data?.summary;
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div>
      <PageHeader title="Employee Ledger" subtitle="Complete, permanent advance history with running balance"
        actions={emp && (
          <>
            {can("admin") && <Button variant="outline" size="sm" onClick={() => setDlg("advance")} data-testid="record-advance-btn"><Plus className="h-4 w-4" /> Advance issued</Button>}
            {can("admin") && <Button variant="outline" size="sm" onClick={() => setDlg("adjustment")} data-testid="record-adjustment-btn"><SlidersHorizontal className="h-4 w-4" /> Adjustment</Button>}
          </>
        )} />
      <div className="no-print max-w-xl mb-5">
        <EmployeePicker value={emp ? { code: emp.code, name: emp.name } : null} onChange={(e) => navigate(`/ledger/${e.code}`)} testid="ledger-employee-picker" autoOpen={!code} />
      </div>
      {!code && <div className="bg-white rounded-xl border border-dashed p-10 text-center text-slate-500"><BookOpen className="h-8 w-8 mx-auto text-slate-300 mb-2" />Search an employee by name or code to open their ledger.</div>}
      {code && data === null && <Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" />}
      {emp && (
        <div className="space-y-5 fade-up">
          <div className="print-only mb-2"><div className="font-bold text-lg">Balaji Sweet House Private Limited</div><div>Employee Advance Ledger</div></div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="ledger-employee-card">
            <div>
              <div className="flex items-center gap-2"><span className="money text-blue-800 font-bold" data-testid="ledger-employee-code">{emp.code}</span>
                <span className={`text-xs rounded-full px-2 py-0.5 ${emp.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{emp.status === "active" ? "Active" : "Inactive"}</span></div>
              <div className="text-xl font-heading font-bold text-slate-900" data-testid="ledger-employee-name">{emp.name}</div>
              <div className="text-sm text-slate-500">{[emp.department, emp.branch].filter(Boolean).join(" · ") || "No department / branch"}</div>
            </div>
            <div className="md:text-right rounded-xl bg-slate-900 text-white px-5 py-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-400">Current outstanding</div>
              <div className="money text-3xl font-extrabold" data-testid="ledger-current-balance">{inr(s.current_balance)}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard label="Opening balance" value={inr(s.opening_balance)} tone="blue" testid="ledger-opening" />
            <StatCard label="Total advances issued" value={inr(s.total_advances)} tone="red" testid="ledger-advances" />
            <StatCard label="Total deductions" value={inr(s.total_deductions)} tone="green" testid="ledger-deductions" />
            <StatCard label="Current outstanding" value={inr(s.current_balance)} testid="ledger-current" />
          </div>
          <div className="no-print flex flex-col lg:flex-row lg:items-center gap-2 justify-between">
            <div className="flex flex-wrap gap-2">
              <DatePicker value={filters.date_from} onChange={(v) => setF("date_from", v)} clearable placeholder="From DD-MM-YYYY" className="w-full sm:w-48 h-10" testid="ledger-date-from" />
              <DatePicker value={filters.date_to} onChange={(v) => setF("date_to", v)} clearable placeholder="To DD-MM-YYYY" className="w-full sm:w-48 h-10" testid="ledger-date-to" />
              <FilterSelect value={filters.type} onChange={(v) => setF("type", v)} options={TYPE_OPTS} placeholder="All types" testid="ledger-type-filter" />
              <div className="relative w-full sm:w-56"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input placeholder="Reference or remarks" value={filters.q} onChange={(e) => setF("q", e.target.value)} className="pl-9 bg-white" data-testid="ledger-search-input" /></div>
            </div>
            <ExportButtons prefix="ledger" onExport={(fmt) => download(`/ledger/${emp.code}/export`, { ...filters, format: fmt })}
              extra={<Button variant="outline" size="sm" onClick={() => window.print()} data-testid="ledger-print-btn"><Printer className="h-4 w-4" /> Print</Button>} />
          </div>
          <LedgerTable entries={data.entries} isAdmin={can("admin")} onReverse={setRev} />
        </div>
      )}
      {emp && ["advance", "adjustment"].map((m) => (
        <AdminTxnDialog key={m} mode={m} employee={emp} balance={s.current_balance} open={dlg === m} onOpenChange={(o) => setDlg(o ? m : "")} onDone={load} />
      ))}
      <ReverseDialog txn={rev} onOpenChange={(o) => !o && setRev(null)} onDone={load} />
    </div>
  );
}
